/**
 * CreateWorkspacePage — the "+ new workspace" flow (sales-led onboarding).
 *
 * Replaces the retired onboarding wizard: one name field, one create button. No connect
 * step: the two doors (Connect GitHub, or install YAGNI Code) carry the ask once the workspace
 * exists. The POST body and response handling match what the wizard sent
 * (name + browser timezone; the partial-success `sessionRefreshRequired` path
 * refetches the app session before leaving, so navigation targets the
 * workspace just created).
 */
import { useCallback, useState } from "react";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { FormField } from "../ui/form-field.js";
import { Input } from "../ui/input.js";

function readBrowserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function CreateWorkspacePage({
  onCreated,
  refreshSession,
}: {
  /** Fired once the workspace exists (and the session points at it). */
  onCreated: () => void;
  /**
   * Refetch the app's auth session. The create route has a partial-success
   * path (`sessionRefreshRequired: true`) where the session cookie was NOT
   * rotated to the new workspace — awaiting this before `onCreated` keeps the
   * navigation pointed at the workspace just created.
   */
  refreshSession?: () => Promise<unknown>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setError(null);
    setBusy(true);
    void (async () => {
      try {
        const res = await fetch("/api/workspaces", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: trimmed,
            timezone: readBrowserTimezone() ?? undefined,
          }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setError(data.error ?? "Failed to create workspace");
          return;
        }
        const result = (await res.json().catch(() => ({}))) as {
          sessionRefreshRequired?: boolean;
        };
        if (result.sessionRefreshRequired) {
          await refreshSession?.();
        }
        onCreated();
      } catch {
        setError("Failed to create workspace");
      } finally {
        setBusy(false);
      }
    })();
  }, [busy, name, onCreated, refreshSession]);

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--canvas-field)",
        padding: 24,
      }}
    >
      <Card variant="staged" className="p-6 flex flex-col gap-4" style={{ width: "100%", maxWidth: 480 }}>
        <h1 style={{ margin: 0, fontSize: "var(--text-lg)", fontWeight: 600 }}>
          Name your workspace
        </h1>
        <FormField label="Workspace name" htmlFor="create-workspace-name">
          <Input
            id="create-workspace-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) submit();
            }}
            placeholder="Acme Corp"
            autoFocus
          />
        </FormField>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Button
            type="button"
            onClick={submit}
            disabled={!name.trim() || busy}
            loading={busy}
            data-testid="create-workspace-submit"
          >
            Create workspace
          </Button>
          {error && (
            <span role="alert" className="text-sm text-error">
              {error}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
